import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "../api/apiClient";
import { DeliveryLinesEditor } from "../components/DeliveryLinesEditor";
import {
  deliveryLinePayload,
  newDeliveryLineDraft,
  type DeliveryLineDraft,
} from "../utils/deliveryLineDrafts";
import type {
  CreateScheduledDeliveryLinePayload,
  CreateScheduledDeliveryPayload,
  ReorderSuggestion,
  Supplier,
} from "../api/types";
import { ForwardDemandPanel } from "../components/ForwardDemandPanel";
import { ReorderSuggestionsPanel } from "../components/ReorderSuggestionsPanel";
import { useAuth } from "../state/AuthContext";
import { basisForPrefill } from "../utils/typedMoneyBasis";
import type { DashboardOutletContext } from "./dashboardOutletContext";

function nowLocalIsoMinute(): string {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 24);
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function DeliveryNewPage() {
  const { user } = useAuth();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const navigate = useNavigate();
  const isOwner = user?.role === "OWNER";

  const [supplierId, setSupplierId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [scheduledFor, setScheduledFor] = useState(nowLocalIsoMinute());
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DeliveryLineDraft[]>([
    newDeliveryLineDraft(),
  ]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(locations[0].id);
    }
  }, [locations, locationId]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!supplierId) throw new Error("Pick a supplier");
      if (!locationId) throw new Error("Pick a shop");
      if (lines.length === 0) throw new Error("Add at least one line");

      const payloadLines: CreateScheduledDeliveryLinePayload[] = lines.map(deliveryLinePayload);

      const payload: CreateScheduledDeliveryPayload = {
        supplierId,
        locationId,
        scheduledFor: new Date(scheduledFor).toISOString(),
        ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: payloadLines,
      };

      const created = await api.createScheduledDelivery(payload);
      navigate(`/app/deliveries/${created.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not schedule delivery");
    } finally {
      setBusy(false);
    }
  };

  const handleLocationChange = (next: string) => {
    setLocationId(next);
    setLines((prev) => prev.map((l) => ({ ...l, zoneId: "" })));
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h2 className="page-title">Schedule a delivery</h2>
        <p className="page-subtitle">
          Pre-load the supplier invoice so the florist can confirm what arrives line by line.
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="card">
        <div className="form-grid">
          <div className="field">
            <label htmlFor="del-supplier">Supplier *</label>
            <select
              id="del-supplier"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">Select a supplier</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="del-location">Shop *</label>
            <select
              id="del-location"
              value={locationId}
              onChange={(e) => handleLocationChange(e.target.value)}
              disabled={!isOwner && locations.length === 1}
            >
              <option value="">Select a shop</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
            {!isOwner ? (
              <small className="hint">
                Managers can only schedule deliveries for their assigned shop.
              </small>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="del-eta">ETA *</label>
            <input
              id="del-eta"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="del-invoice">Invoice number (optional)</label>
            <input
              id="del-invoice"
              type="text"
              maxLength={120}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="e.g. HFB-20260512"
            />
          </div>
          <div className="field full">
            <label htmlFor="del-notes">Notes (optional)</label>
            <input
              id="del-notes"
              type="text"
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      </div>

      {locationId ? (
        <ReorderSuggestionsPanel
          locationId={locationId}
          onPrefill={(rows: ReorderSuggestion[]) => {
            const drafts = rows.map((r) => ({
              ...newDeliveryLineDraft(),
              itemId: r.itemId,
              expectedQty: r.suggestedQty,
              expectedUnitCostEur: r.lastUnitCostEur ?? "",
              // G-121 S3: the last batch's cost is stored before IVA.
              moneyBasis: basisForPrefill(r.lastUnitCostEur),
            }));
            // Replace untouched blank drafts; append to real ones.
            setLines((prev) => {
              const kept = prev.filter((l) => l.itemId !== "");
              return [...kept, ...drafts.filter((d) => !kept.some((k) => k.itemId === d.itemId))];
            });
          }}
        />
      ) : null}

      <ForwardDemandPanel />

      <div className="card">
        <DeliveryLinesEditor
          locationId={locationId}
          lines={lines}
          onChange={setLines}
        />
      </div>

      <div
        className="card"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div className="button-row">
          <button
            type="button"
            className="button plain"
            onClick={() => navigate("/app/deliveries")}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button"
            disabled={busy || !supplierId || !locationId || lines.length === 0}
            onClick={() => void submit()}
          >
            {busy ? "Scheduling…" : "Schedule delivery"}
          </button>
        </div>
      </div>
    </section>
  );
}
